import { useEffect, useRef } from "react";
import { THREAD_ID_PARAM } from "duck/ui/chat/constants";
import { useDuck } from "duck/ui/chat/contexts/DuckContext";
import { useLocation, useNavigate } from "react-router";

/**
 * This hook opens the thread a link points to with `?thread_id=<id>`, on any page.
 * Only the user's own threads can be opened. The param is used once, then removed
 * from the URL.
 */
export const useDuckLinkParams = (
  selectThread: (threadId: string, refetchThreads: boolean) => void
) => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const { setOpen } = useDuck();
  // The last thread a link opened. A link can pass through more than one page (like
  // `/` and then the tenant's root page) before the param is removed, and it should
  // open the thread only once.
  const handledLinkRef = useRef<string | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    if (!searchParams.has(THREAD_ID_PARAM)) {
      // No thread param left: forget the last link, so opening it again works.
      handledLinkRef.current = null;

      return;
    }

    const threadId = searchParams.get(THREAD_ID_PARAM)?.trim();
    if (threadId && handledLinkRef.current !== threadId) {
      handledLinkRef.current = threadId;
      selectThread(threadId, true);
      setOpen(true);
    }

    searchParams.delete(THREAD_ID_PARAM);
    const nextSearch = searchParams.toString();
    navigate(
      { pathname, search: nextSearch ? `?${nextSearch}` : "", hash },
      { replace: true }
    );
  }, [pathname, search, hash, navigate, selectThread, setOpen]);
};
